import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Navigate } from '@rocketlane/rocket-router';
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosHeaders } from 'axios';
import { isMobile } from 'react-device-detect';
import { FormattedMessage } from 'react-intl';
import { Button } from 'components/facade/button';
import { triggerOtp } from 'components/pages/two-factor-auth/utils';
import { useQueryParams } from 'hooks/use-query-params';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { ApiKey } from 'services';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { getTokenMeta, isLinkMagicToken, PermissionType } from 'utils/token';
import { isSameAppOrigin, isTrustedApiUrl } from 'utils/url-safety';
import { ErrorIcon, FullPageResult, LoadingIcon, SuccessIcon } from './styles';
export const LinkActionPage = ()=>{
    var _apiUrlParams_get, _ref;
    var _decoded_scopes;
    // Public route — dismiss the static #rl-boot-skeleton overlay from
    // public/index.html so it doesn't bleed through this page. See Login.
    useDismissBootSkeleton();
    const queryParams = useQueryParams();
    const api_url = queryParams.get('api_url') || '';
    const splitApi = api_url.split('?');
    const apiUrlParams = new URLSearchParams(splitApi[1]);
    const api_token = (_apiUrlParams_get = apiUrlParams.get('token')) !== null && _apiUrlParams_get !== void 0 ? _apiUrlParams_get : '';
    const { decoded } = getTokenMeta(api_token);
    // action_context_id is something we used to deduce what text to show
    // on every possible scenarios.
    // For ex: the action_context_id will be 1002 for task in progress,
    // and we show messages linked to that context.
    const action_context_id = queryParams.get('action_context_id') || '';
    const primary_link = queryParams.get('primary_link') || '';
    // SECURITY: api_url is fully attacker-controllable via the query string and
    // we attach the session api-key to the request. Only call THIS tenant's own
    // API host; only follow same-app-origin CTA links. Validated against the
    // backend's email-link host derivation (see utils/url-safety).
    const isApiUrlTrusted = isTrustedApiUrl(api_url);
    const isPrimaryLinkSafe = isSameAppOrigin(primary_link);
    const isOtpRequired = decoded === null || decoded === void 0 ? void 0 : (_decoded_scopes = decoded.scopes) === null || _decoded_scopes === void 0 ? void 0 : _decoded_scopes.includes(PermissionType.UserOtp);
    const isLoggedIn = ApiKey.exists();
    const shouldRedirectToOtpPage = isOtpRequired && !isLoggedIn;
    // LINK_MAGIC → X-Magic-Token header, stripped from the URL (crashes the legacy parser);
    // legacy HS256 → ?token= query.
    const isLinkMagic = isLinkMagicToken(api_token);
    const stripTokenFromUrl = (url)=>{
        const [base, query] = url.split('?');
        if (!query) {
            return url;
        }
        const params = new URLSearchParams(query);
        params.delete('token');
        const rest = params.toString();
        return rest ? `${base}?${rest}` : base;
    };
    const actionUrl = isLinkMagic ? stripTokenFromUrl(api_url) : api_url;
    const { data: response, status } = useQuery([
        'link-action'
    ], ()=>axios.post(actionUrl, undefined, {
            headers: isLinkMagic ? new AxiosHeaders({
                'X-Magic-Token': api_token
            }) : ApiKey.header()
        }), {
        retry: false,
        refetchOnWindowFocus: false,
        enabled: isApiUrlTrusted && !shouldRedirectToOtpPage
    });
    const taskName = (_ref = response === null || response === void 0 ? void 0 : response.data.taskName) !== null && _ref !== void 0 ? _ref : '';
    if (!isApiUrlTrusted) {
        // Untrusted destination — never attach the api-key and never replay it
        // into the OTP flow. Show the generic error result instead.
        return /*#__PURE__*/ _jsx(FullPageResult, {
            title: /*#__PURE__*/ _jsx(FormattedMessage, {
                id: "link_action.error_message"
            }),
            subTitle: /*#__PURE__*/ _jsx(FormattedMessage, {
                id: "link_action.error_message.extra"
            }),
            icon: /*#__PURE__*/ _jsx(ErrorIcon, {})
        });
    }
    if (shouldRedirectToOtpPage) {
        triggerOtp(api_token);
        return /*#__PURE__*/ _jsx(Navigate, {
            replace: true,
            to: `${ROUTE_PATHS.MAGIC_LINK}/${ROUTE_PATHS.TWO_FA}`,
            state: {
                token: api_token,
                taskStatusUpdateAction: {
                    otpEnabled: true,
                    apiUrl: api_url,
                    redirectUrl: primary_link
                }
            }
        });
    }
    switch(status){
        case 'loading':
            return /*#__PURE__*/ _jsx(FullPageResult, {
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(FormattedMessage, {
                            // TODO: use context specific message as loading_message can be for phase also when we're doing it.
                            id: "link_action.loading_message"
                        }),
                        /*#__PURE__*/ _jsx("strong", {
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: `link_action.context.${action_context_id}`
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "link_action.loading_message.postfix"
                        })
                    ]
                }),
                icon: /*#__PURE__*/ _jsx(LoadingIcon, {
                    size: "large"
                })
            });
        case 'error':
            return /*#__PURE__*/ _jsx(FullPageResult, {
                extra: !isMobile && isPrimaryLinkSafe ? /*#__PURE__*/ _jsx(Button, {
                    iconPlacement: "right",
                    type: "primary",
                    size: "large",
                    icon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                    href: primary_link,
                    rel: "noopener noreferrer",
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "link_action.cta"
                    })
                }) : null,
                subTitle: /*#__PURE__*/ _jsx(FormattedMessage, {
                    id: "link_action.error_message.extra"
                }),
                title: /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "link_action.error_message"
                    })
                }),
                icon: /*#__PURE__*/ _jsx(ErrorIcon, {})
            });
        case 'success':
            return /*#__PURE__*/ _jsx(FullPageResult, {
                extra: !isMobile && isPrimaryLinkSafe ? /*#__PURE__*/ _jsx(Button, {
                    iconPlacement: "right",
                    type: "primary",
                    size: "large",
                    icon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                    href: primary_link,
                    rel: "noopener noreferrer",
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "link_action.cta"
                    })
                }) : null,
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        `"${taskName}"`,
                        " ",
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsxs("strong", {
                            children: [
                                /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "link_action.success_message"
                                }),
                                /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: `link_action.context.${action_context_id}`
                                })
                            ]
                        })
                    ]
                }),
                icon: /*#__PURE__*/ _jsx(SuccessIcon, {})
            });
        default:
            return null;
    }
};
